/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { AdvertisementBanner } from '../models/advertisement-banner';
import { MerchantCountResponse } from '../models/merchant-count-response';

/**
 * Public endpoint which can reach without authorization
 */
@Injectable({
  providedIn: 'root',
})
export class PublicEndpointsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getCountOfProducts
   */
  static readonly GetCountOfProductsPath = '/public/products_count';

  /**
   * Count products.
   *
   * Finds and returns the count of products
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountOfProducts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountOfProducts$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<number>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PublicEndpointsService.GetCountOfProductsPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: parseFloat(String((r as HttpResponse<any>).body)),
          }) as StrictHttpResponse<number>;
        })
      );
  }

  /**
   * Count products.
   *
   * Finds and returns the count of products
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCountOfProducts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountOfProducts(params?: {}, context?: HttpContext): Observable<number> {
    return this.getCountOfProducts$Response(params, context).pipe(
      map((r: StrictHttpResponse<number>) => r.body as number)
    );
  }

  /**
   * Path part for operation getPackagingUnitAdvertisementBanners
   */
  static readonly GetPackagingUnitAdvertisementBannersPath =
    '/public/packaging_unit_advertisement_banner_logos';

  /**
   * Gets packaging unit advertisement banners from packaging-unit-advertisement-banners folder.
   *
   * Finds and returns the url and the id and label of the packaging unit.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackagingUnitAdvertisementBanners()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagingUnitAdvertisementBanners$Response(
    params?: {
      limit?: number;
      randomize?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<AdvertisementBanner>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PublicEndpointsService.GetPackagingUnitAdvertisementBannersPath,
      'get'
    );
    if (params) {
      rb.query('limit', params.limit, {});
      rb.query('randomize', params.randomize, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<AdvertisementBanner>>;
        })
      );
  }

  /**
   * Gets packaging unit advertisement banners from packaging-unit-advertisement-banners folder.
   *
   * Finds and returns the url and the id and label of the packaging unit.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackagingUnitAdvertisementBanners$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagingUnitAdvertisementBanners(
    params?: {
      limit?: number;
      randomize?: boolean;
    },
    context?: HttpContext
  ): Observable<Array<AdvertisementBanner>> {
    return this.getPackagingUnitAdvertisementBanners$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<Array<AdvertisementBanner>>) =>
          r.body as Array<AdvertisementBanner>
      )
    );
  }

  /**
   * Path part for operation getCountOfMerchants
   */
  static readonly GetCountOfMerchantsPath = '/public/merchants_count';

  /**
   * Count merchants.
   *
   * Finds and returns the count of the registrated merchants
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountOfMerchants()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountOfMerchants$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantCountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PublicEndpointsService.GetCountOfMerchantsPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantCountResponse>;
        })
      );
  }

  /**
   * Count merchants.
   *
   * Finds and returns the count of the registrated merchants
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCountOfMerchants$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountOfMerchants(
    params?: {},
    context?: HttpContext
  ): Observable<MerchantCountResponse> {
    return this.getCountOfMerchants$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantCountResponse>) =>
          r.body as MerchantCountResponse
      )
    );
  }

  /**
   * Path part for operation getMerchantsLogoUrls
   */
  static readonly GetMerchantsLogoUrlsPath = '/public/banner_logos';

  /**
   * Gets images URL from banner-rotator folder.
   *
   * Finds and returns the valid files from banner-rotator-folder.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantsLogoUrls()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantsLogoUrls$Response(
    params?: {
      limit?: number;
      randomize?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<string>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PublicEndpointsService.GetMerchantsLogoUrlsPath,
      'get'
    );
    if (params) {
      rb.query('limit', params.limit, {});
      rb.query('randomize', params.randomize, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<string>>;
        })
      );
  }

  /**
   * Gets images URL from banner-rotator folder.
   *
   * Finds and returns the valid files from banner-rotator-folder.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantsLogoUrls$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantsLogoUrls(
    params?: {
      limit?: number;
      randomize?: boolean;
    },
    context?: HttpContext
  ): Observable<Array<string>> {
    return this.getMerchantsLogoUrls$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<string>>) => r.body as Array<string>)
    );
  }
}
